// store/slices/globalSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import formatDate from "../../utils/services";
import axiosInstance from "../../helpers/axios";
import { authConstants } from "../../actions/constants";

export const fetchEvents = createAsyncThunk("global/fetchEvents", async (options) => {
  const { data } = await axiosInstance.get("/company-events", { memberRequest: true, params: options?.currentFinancialYear ? { currentFinancialYear: true } : { futureOnly: true } });
  return data;
});

export const fetchCalendarHolidays = createAsyncThunk("global/fetchCalendarHolidays", async () => {
  const { data } = await axiosInstance.get("/holidays", { params: { futureOnly: true }, memberRequest: true });
  return data;
});

// Async thunk for fetching holidays
export const fetchHolidays = createAsyncThunk(
  "global/fetchHolidays",
  async () => {
    const response = await axiosInstance.get(`/holidays`);
    const holidaysData = response.data;
    const holidays = response.data.map((holiday) => formatDate(holiday.date));

    return {
      holidaysData,
      holidays,
    };
  }
);

// Async thunk for fetching designations
export const fetchDesignations = createAsyncThunk(
  "global/fetchDesignations",
  async () => {
    const designationsData = await axiosInstance.get(`/designations`);
    const allDesignations = Array.from(
      new Set([...designationsData.data.data.map((d) => d.name), "Vendor"])
    );
    const billedDesignations = designationsData.data.data
      .filter((designation) => designation.billed)
      .map((d) => d.name);
    const unBilledDesignations = Array.from(
      new Set([
        ...designationsData.data.data
          .filter((designation) => !designation.billed)
          .map((d) => d.name),
        "Vendor",
      ])
    );

    return {
      designations: designationsData.data.data,
      allDesignations,
      billedDesignations,
      unBilledDesignations,
    };
  }
);

const globalSlice = createSlice({
  name: "global",
  initialState: {
    holidays: [],
    calendarHolidaysData: [],
    events: [],
    eventsLoading: false,
    eventsError: null,
    eventsRequestId: null,
    holidaysData: [],
    designations: [],
    allDesignations: [],
    billedDesignations: [],
    unBilledDesignations: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendarHolidays.fulfilled, (state, action) => {
        state.calendarHolidaysData = action.payload;
      })
      .addCase(authConstants.LOGOUT_SUCCESS, (state) => {
        state.events = [];
        state.eventsLoading = false;
        state.eventsError = null;
        state.eventsRequestId = null;
      })
      .addCase(fetchEvents.pending, (state, action) => {
        state.eventsRequestId = action.meta.requestId;
        state.eventsLoading = true;
        state.eventsError = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        if (state.eventsRequestId !== action.meta.requestId) return;
        state.events = action.payload;
        state.eventsLoading = false;
        state.eventsRequestId = null;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        if (state.eventsRequestId !== action.meta.requestId) return;
        state.eventsLoading = false;
        state.eventsError = action.error.message;
        state.eventsRequestId = null;
      })
      // Handle holidays
      .addCase(fetchHolidays.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.holidays = action.payload.holidays;
        state.holidaysData = action.payload.holidaysData;
        state.loading = false;
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      // Handle designations
      .addCase(fetchDesignations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        state.designations = action.payload.designations;
        state.allDesignations = action.payload.allDesignations;
        state.billedDesignations = action.payload.billedDesignations;
        state.unBilledDesignations = action.payload.unBilledDesignations;
        state.loading = false;
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });
  },
});

export default globalSlice.reducer;
